import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { LayoutDashboard, Package, Truck, Settings2, MapPin, BookOpen, PackageCheck } from 'lucide-react';
import usePermissions from '../../hooks/usePermissions';
import { useShortcutTarget } from '../../contexts/ShortcutsContext';
import { INVENTORY_TAB_META, autoColor, metaLabel } from '../../constants/shortcutTargets';
import InventoryDashboard from './InventoryDashboard';
import AssetsList from './AssetsList';
import AssignmentsOverview from './AssignmentsOverview';
import SuppliersList from './SuppliersList';
import AssetModelsRegister from './AssetModelsRegister';
import InventorySettings from './InventorySettings';
import DeliveriesList from './DeliveriesList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InventoryPage = ({ employees, projects, navigateTo, navContext }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const [categories, setCategories] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [models, setModels] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [inspectionTypes, setInspectionTypes] = useState([]);
  const [damageSeverities, setDamageSeverities] = useState([]);
  const [repairStatuses, setRepairStatuses] = useState([]);
  const [damageAffected, setDamageAffected] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  // Set when the user chooses to book a batch in by scanning: the Assets tab
  // opens the plate scanner and every tool lands in this delivery.
  const [bookingDelivery, setBookingDelivery] = useState(null);

  const fetchCategories = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/categories`); setCategories(r.data); } catch {} }, []);
  const fetchConditions = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/conditions`); setConditions(r.data); } catch {} }, []);
  const fetchSuppliers = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/suppliers`); setSuppliers(r.data); } catch {} }, []);
  const fetchManufacturers = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/manufacturers`); setManufacturers(r.data); } catch {} }, []);
  const fetchModels = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/asset-models`); setModels(r.data); } catch {} }, []);
  const fetchCustomers = useCallback(async () => { try { const r = await axios.get(`${API}/customers`); setCustomers(r.data || []); } catch {} }, []);
  const fetchDashboard = useCallback(async () => { if (!can('inventory_dashboard.view')) return; try { const r = await axios.get(`${API}/inventory/dashboard`); setDashboard(r.data); } catch {} }, [can]);
  const fetchInspectionTypes = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/config/inspection_types`); setInspectionTypes(r.data); } catch {} }, []);
  const fetchDamageSeverities = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/config/damage_severities`); setDamageSeverities(r.data); } catch {} }, []);
  const fetchRepairStatuses = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/config/repair_statuses`); setRepairStatuses(r.data); } catch {} }, []);
  const fetchDamageAffected = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/config/damage_affected`); setDamageAffected(r.data); } catch {} }, []);
  const fetchStorageLocations = useCallback(async () => { try { const r = await axios.get(`${API}/inventory/config/storage_locations`); setStorageLocations(r.data); } catch {} }, []);
  const fetchCurrencies = useCallback(async () => { try { const r = await axios.get(`${API}/settings`); setCurrencies(r.data?.currencies || ['PLN', 'EUR', 'GBP', 'USD']); } catch {} }, []);

  useEffect(() => {
    fetchCategories(); fetchConditions(); fetchSuppliers(); fetchManufacturers();
    fetchDashboard(); fetchInspectionTypes(); fetchDamageSeverities(); fetchRepairStatuses();
    fetchDamageAffected(); fetchStorageLocations();
    fetchCurrencies(); fetchModels(); fetchCustomers();
  }, [fetchCategories, fetchConditions, fetchSuppliers, fetchManufacturers, fetchDashboard, fetchInspectionTypes, fetchDamageSeverities, fetchRepairStatuses, fetchDamageAffected, fetchStorageLocations, fetchCurrencies, fetchModels, fetchCustomers]);

  const isAdmin = can('inventory.edit');
  // Each tab now answers to its own permission row, so a storeman can keep
  // the catalogue without reaching the configuration lists.
  const tabs = {
    dashboard: can('inventory_dashboard.view'),
    assets: can('inventory.view'),
    assignments: can('inventory_assignments.view'),
    suppliers: can('inventory_suppliers.view'),
    // Deliveries are a grouping of assets, so they answer to the asset
    // register's own permission rather than a new key every role must be
    // granted by hand.
    deliveries: can('inventory.view'),
    catalogue: can('inventory_catalogue.view'),
    settings: can('inventory_settings.view'),
  };
  const configData = {
    manufacturers, currencies, inspectionTypes, damageSeverities, repairStatuses,
    models, customers, projects: projects || [], conditions, storageLocations, suppliers,
  };

  // Arriving from a dashboard shortcut / cross-module jump
  useEffect(() => {
    if (navContext?.invSubTab) setActiveTab(navContext.invSubTab);
  }, [navContext]);

  const invMeta = INVENTORY_TAB_META[activeTab] || { icon: 'inventory', perms: ['inventory.view'] };
  useShortcutTarget({
    tab: 'inventory',
    context: { invSubTab: activeTab },
    label: metaLabel(invMeta, t) || activeTab,
    subtitle: t('nav.inventory'),
    icon: invMeta.icon,
    color: autoColor(`inventory-${activeTab}`),
    perms: invMeta.perms,
  }, 1);

  // Land on the first tab this role can actually see
  useEffect(() => {
    if (!tabs[activeTab]) {
      const first = Object.keys(tabs).find((k) => tabs[k]);
      if (first) setActiveTab(first);
    }
  }, [tabs, activeTab]);

  return (
    <div data-testid="inventory-page">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-1 mb-4">
          {tabs.dashboard && (
            <TabsTrigger value="dashboard" className="flex items-center gap-1.5" data-testid="inventory-tab-dashboard">
              <LayoutDashboard className="w-4 h-4" /><span>{t('inv.tabs.dashboard')}</span>
            </TabsTrigger>
          )}
          {tabs.assets && (
            <TabsTrigger value="assets" className="flex items-center gap-1.5" data-testid="inventory-tab-assets">
              <Package className="w-4 h-4" /><span>{t('inv.tabs.assets')}</span>
            </TabsTrigger>
          )}
          {tabs.assignments && (
            <TabsTrigger value="assignments" className="flex items-center gap-1.5" data-testid="inventory-tab-assignments">
              <MapPin className="w-4 h-4" /><span>{t('inv.tabs.assignments')}</span>
            </TabsTrigger>
          )}
          {tabs.suppliers && (
            <TabsTrigger value="suppliers" className="flex items-center gap-1.5" data-testid="inventory-tab-suppliers">
              <Truck className="w-4 h-4" /><span>{t('inv.tabs.suppliers')}</span>
            </TabsTrigger>
          )}
          {tabs.deliveries && (
            <TabsTrigger value="deliveries" className="flex items-center gap-1.5" data-testid="inventory-tab-deliveries">
              <PackageCheck className="w-4 h-4" /><span>{t('inv.tabs.deliveries')}</span>
            </TabsTrigger>
          )}
          {tabs.catalogue && (
            <TabsTrigger value="catalogue" className="flex items-center gap-1.5" data-testid="inventory-tab-catalogue">
              <BookOpen className="w-4 h-4" /><span>{t('inv.tabs.catalogue')}</span>
            </TabsTrigger>
          )}
          {tabs.settings && (
            <TabsTrigger value="settings" className="flex items-center gap-1.5" data-testid="inventory-tab-settings">
              <Settings2 className="w-4 h-4" /><span>{t('inv.tabs.settings')}</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="dashboard">
          {tabs.dashboard && <InventoryDashboard dashboard={dashboard} onRefresh={fetchDashboard} />}
        </TabsContent>
        <TabsContent value="assets">
          {tabs.assets && (
          <AssetsList
            bookingDelivery={bookingDelivery}
            onBookingDeliveryDone={() => setBookingDelivery(null)}
            categories={categories} conditions={conditions} suppliers={suppliers}
            employees={employees} projects={projects} isAdmin={isAdmin}
            config={configData} onDataChange={fetchDashboard}
            navigateTo={navigateTo}
          />
          )}
        </TabsContent>
        <TabsContent value="assignments">
          {tabs.assignments && (
          <AssignmentsOverview
            navigateTo={navigateTo}
            categories={categories}
            conditions={conditions}
            suppliers={suppliers}
            employees={employees}
            projects={projects}
            isAdmin={can('inventory_assignments.edit')}
            config={configData}
          />
          )}
        </TabsContent>
        <TabsContent value="suppliers">
          {tabs.suppliers && <SuppliersList suppliers={suppliers} isAdmin={can('inventory_suppliers.edit')} onRefresh={fetchSuppliers} />}
        </TabsContent>
        <TabsContent value="deliveries">
          {tabs.deliveries && (
            <DeliveriesList
              onBookInto={(d) => { setBookingDelivery(d); setActiveTab('assets'); }}
              suppliers={suppliers}
              storageLocations={storageLocations}
              currencies={currencies}
              isAdmin={isAdmin}
              navigateTo={navigateTo}
            />
          )}
        </TabsContent>
        {tabs.catalogue && (
          <TabsContent value="catalogue">
            <AssetModelsRegister models={models} manufacturers={manufacturers}
              categories={categories} onRefresh={fetchModels} />
          </TabsContent>
        )}
        {tabs.settings && (
          <TabsContent value="settings">
            <InventorySettings
              categories={categories} conditions={conditions} manufacturers={manufacturers}
              inspectionTypes={inspectionTypes} damageSeverities={damageSeverities} repairStatuses={repairStatuses}
              damageAffected={damageAffected} storageLocations={storageLocations}
              onCategoriesChange={fetchCategories} onConditionsChange={fetchConditions}
              onManufacturersChange={fetchManufacturers}
              onInspectionTypesChange={fetchInspectionTypes}
              onDamageSeveritiesChange={fetchDamageSeverities}
              onRepairStatusesChange={fetchRepairStatuses}
              onDamageAffectedChange={fetchDamageAffected}
              onStorageLocationsChange={fetchStorageLocations}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default InventoryPage;
